import { Component, Input, OnChanges } from '@angular/core';

export type AvatarPreset = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'fluid' | string;

@Component({
  selector: 'app-initial-avatar',
  templateUrl: './avatar-initials.component.html',
  styleUrls: ['./avatar-initials.component.css'],
})
export class InitialAvatarComponent implements OnChanges {
  @Input() firstName!: string;
  @Input() lastName?: string;
  @Input() className: string = '';
  @Input() useResponsive: boolean = false;
  @Input() borderRadius: string = 'rounded-full';
  @Input() colorless: boolean = false;


  _size: number | string = 48;
  @Input() set size(value: number | string) {
    if (value) this._size = value;
  }
  get size() { return this._size; }

  @Input() set preset(value: AvatarPreset) {
    this.setPreset(value);
  }

  _src: string | null = null;
  @Input() set src(value: string | undefined | null) {
    this._src = value || null;
  }
  get src() { return this._src; }

  handleError() {
    this._src = null; // Fallback to initials
  }

  initials: string = '?';
  bgColor: string = '#ccc';
  fontSize: string = '1rem';

  private readonly colors = [
    '#F87171', // red-400
    '#FBBF24', // yellow-400
    '#34D399', // green-400
    '#60A5FA', // blue-400
    '#A78BFA', // purple-400
    '#F472B6', // pink-400
    '#38BDF8', // sky-400
    '#FB923C', // orange-400
  ];

  constructor() { }

  ngOnChanges(): void {
    this.updateInitials();
  }

  setPreset(preset: AvatarPreset) {
    switch (preset) {
      case 'xs': this._size = 24; this.fontSize = '0.75rem'; break;
      case 'sm': this._size = 32; this.fontSize = '0.875rem'; break;
      case 'md': this._size = 48; this.fontSize = '1.25rem'; break;
      case 'lg': this._size = 64; this.fontSize = '1.5rem'; break;
      case 'xl': this._size = 96; this.fontSize = '2.25rem'; break;
      case 'fluid':
        this.useResponsive = true;
        this._size = 0;
        this.fontSize = '2.5rem';
        break;
      default:
        // Allow custom number passed as string
        if (!isNaN(Number(preset))) {
          this._size = Number(preset);
          this.fontSize = (this._size / 2.4) + 'px';
        }
        break;
    }
  }

  updateInitials() {
    if (!this.firstName) return;

    let fName = this.firstName;
    let lName = this.lastName ?? '';

    // Handle full name passed in firstName
    if (!this.lastName && this.firstName.includes(' ')) {
      const parts = this.firstName.trim().split(' ');
      fName = parts[0];
      lName = parts[parts.length - 1];
    }

    this.initials = `${fName?.[0] ?? ''}${lName?.[0] ?? ''}`.toUpperCase();

    if (this.colorless) {
      this.bgColor = '#F3F4F6'; // gray-100 or neutral light color
      // You might to adjust text color too if needed, but keeping it simple for now
    } else {
      const colorIndex = this.stringToColorIndex(fName + lName);
      this.bgColor = this.colors[colorIndex];
    }

    // Recalculate font size if using numeric size and not fluid
    if (!this.useResponsive && typeof this._size === 'number' && this._size > 0) {
      this.fontSize = (this._size / 2.4) + 'px';
    }
  }

  private stringToColorIndex(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % this.colors.length;
  }
}
